// src/app/core/auth/auth.service.ts
import { Injectable, signal, computed, inject } from '@angular/core';
import { PermissionService } from './permission.service';
import type { AuthResponse } from '../models/auth.model';

const SESSION_KEY = 'bel_session_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly permissionService = inject(PermissionService);

  // ── Signals de sesión ─────────────────────────────────────────────────────
  private readonly _currentUser        = signal<AuthResponse | null>(this.loadFromSession());
  private readonly _token              = signal<string | null>(this.loadTokenFromSession());
  private readonly _shouldRenewPassword = signal<boolean>(false);

  /** Usuario autenticado actual — null si no hay sesión */
  readonly currentUser = this._currentUser.asReadonly();

  /** Token de autenticación (base64EncodedAuthenticationKey) */
  readonly token = this._token.asReadonly();

  /** true si hay un usuario autenticado con token válido */
  readonly isAuthenticated = computed(
    () => this._currentUser() !== null && this._token() !== null
  );

  /** ID del cliente activo — siempre clients[0] (multipleClientsPerUser no implementado) */
  readonly clientId = computed(() => {
    const user = this._currentUser();
    if (!user || !user.clients || user.clients.length === 0) return null;
    // Comportamiento definido: siempre clients[0].
    // multipleClientsPerUser controla la UI de selección, no este valor.
    return user.clients[0];
  });

  /** ID del usuario para operaciones de perfil */
  readonly userId = computed(() => this._currentUser()?.userId ?? null);

  /** ID de la oficina — requerido en ChangePasswordRequest */
  readonly officeId = computed(() => this._currentUser()?.officeId ?? null);

  /** true si el servidor exige un cambio de contraseña antes de continuar */
  readonly shouldRenewPassword = this._shouldRenewPassword.asReadonly();

  /**
   * Inicializa la sesión con la respuesta del servidor tras un login exitoso.
   * Llamado por FineractApiService.authenticate() a través de tap().
   * NO llamar directamente desde componentes.
   * @internal
   */
  initializeSession(response: AuthResponse): void {
    // Almacenar datos de usuario en sessionStorage (no el token — se reconstruye)
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(response));

    // Actualizar Signals
    this._currentUser.set(response);
    this._token.set(response.base64EncodedAuthenticationKey);
    this._shouldRenewPassword.set(response.shouldRenewPassword ?? false);

    // Cargar permisos en PermissionService
    this.permissionService.setPermissions(response.permissions ?? []);
  }

  /**
   * Marca el cambio de contraseña como completado.
   * Llamado por ChangePasswordComponent tras un PUT exitoso.
   * El token sigue siendo válido — no se requiere nuevo login.
   */
  clearShouldRenewPassword(): void {
    this._shouldRenewPassword.set(false);
    // Actualizar sessionStorage para reflejar el cambio
    const user = this._currentUser();
    if (user) {
      const updated = { ...user, shouldRenewPassword: false };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
      this._currentUser.set(updated);
    }
  }

  /**
   * Cierra la sesión completamente.
   * - Limpia sessionStorage (NO localStorage — contiene solo idioma)
   * - Resetea todos los Signals
   * - Limpia los permisos
   * - BroadcastChannel se maneja en SessionService
   */
  logout(): void {
    // Limpiar sessionStorage
    sessionStorage.removeItem(SESSION_KEY);

    // Resetear Signals
    this._currentUser.set(null);
    this._token.set(null);
    this._shouldRenewPassword.set(false);

    // Limpiar permisos
    this.permissionService.clearPermissions();
  }

  /** Restaura el usuario desde sessionStorage al recargar la página */
  private loadFromSession(): AuthResponse | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as AuthResponse;
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
  }

  /** Restaura el token desde el usuario en sessionStorage */
  private loadTokenFromSession(): string | null {
    const user = this.loadFromSession();
    return user?.base64EncodedAuthenticationKey ?? null;
  }
}
